import React from 'react'
import { CheckCircle2, PiggyBank } from 'lucide-react'
import { money } from '../financial/formatting'
import { BALANCE_TITLE } from './model'

// The advance and how much of it has been paid down. `original`, `applied`
// and `remaining` are a present fact about the agreement and are the same
// whatever range is picked; only `applied_in_range` follows the filter, so
// it is set apart from the rest rather than sitting inside the bar's caption
// where it would read as part of the same figure.
export default function BalanceCard({ balance, split }) {
  const original = Number(balance.original || 0)
  const applied = Number(balance.applied || 0)
  const pct = original > 0 ? Math.min(100, Math.max(0, (applied / original) * 100)) : 0
  const cleared = Boolean(balance.cleared_at)
  const clearedOn = cleared ? fmtLongDay(String(balance.cleared_at).slice(0, 10)) : null

  return (
    <section className="card" aria-label={BALANCE_TITLE} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={iconBox}>
          <PiggyBank size={16} />
        </div>
        <h2 style={sectionTitle}>{BALANCE_TITLE}</h2>
      </div>
      <p data-testid="balance-explainer" style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--text-secondary)', maxWidth: 720 }}>
        {cleared ? clearedExplainer(clearedOn) : splitExplainer(split)}
      </p>

      <div className="pd-split" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: 'ui-monospace, monospace', fontSize: 32, fontWeight: 800,
            color: 'var(--text-primary)', lineHeight: 1.1,
          }}>
            {money(balance.remaining)}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            remaining of {money(original)}
          </div>
        </div>
        <div style={{
          padding: '10px 14px', borderRadius: 'var(--radius)',
          background: 'var(--bg-raised)', minWidth: 0,
        }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>
            Applied in this period
          </div>
          <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
            {money(balance.applied_in_range)}
          </div>
        </div>
      </div>

      <div>
        <div
          role="progressbar"
          aria-label="Balance paid down"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
          style={{ height: 10, borderRadius: 999, background: 'var(--bg-raised)', overflow: 'hidden' }}
        >
          <div style={{
            width: `${pct}%`, height: '100%', borderRadius: 999,
            background: cleared ? 'var(--green)' : 'var(--amber)',
            transition: 'width var(--transition)',
          }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Applied so far: <strong style={{ color: 'var(--text-primary)' }}>{money(applied)}</strong>
          </span>
          {cleared && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--green)' }}>
              <CheckCircle2 size={13} />
              Cleared
            </span>
          )}
        </div>
      </div>
    </section>
  )
}

// The terms come from the `split` block the backend sends next to `balance`,
// not from constants here, so the sentence can't describe a different split
// than the one the figures were computed with. The fallbacks are the
// agreement's actual terms, for the case where `split` is ever missing.
const fmtShare = (v) => `${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}%`

// "October 1, 2026", spelled out: this sentence is read as prose, not as a
// table cell. Split by hand for the same reason as financial/formatting's
// fmtDay - a YYYY-MM-DD from the backend must not shift with the timezone.
const fmtLongDay = (ymd) => {
  const [y, m, d] = String(ymd).split('-').map(Number)
  if (!y || !m || !d) return String(ymd)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function splitExplainer(split) {
  const start = fmtLongDay(split?.accrual_start || '2026-10-01')
  const partner = fmtShare(split?.partner_share_pct ?? 60)
  const toBalance = fmtShare(split?.balance_share_pct ?? 40)
  return `Starting ${start}, ${partner} of your lead value is paid to you and the remaining ${toBalance} is applied to this balance until it is cleared. Every lead shows its full value, so the dashboard always matches your payments.`
}

const clearedExplainer = (on) =>
  `Balance cleared on ${on}. Value since then is held until new terms are agreed.`

const iconBox = {
  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--amber-bg)', color: 'var(--amber)',
}

const sectionTitle = {
  margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: 0.6,
  color: 'var(--text-muted)', textTransform: 'uppercase',
}
